import { format } from 'date-fns';
import { MutableRefObject } from 'react';

const generateAutofillPrefix = (userName: string | null): string => {
    const currentDate = format(new Date(), 'dd-MM-yyyy');
    const displayName = userName || 'Unknown User';
    return `${currentDate} (${displayName}): `;
};

// Helper to position cursor after autofill
const setCursorPosition = (inputRef: MutableRefObject<HTMLTextAreaElement | null>, position: number): void => {
    setTimeout(() => {
        if (inputRef.current) {
            inputRef.current.setSelectionRange(position, position);
        }
    }, 0);
};

const shouldAutofillAtCursor = (value: string, cursorPosition: number): boolean => {
    // At start of field - check if first line is empty
    if (cursorPosition === 0) {
        const firstLineEnd = value.indexOf('\n');
        const firstLine = firstLineEnd === -1 ? value : value.substring(0, firstLineEnd);
        return firstLine === '';
    }

    // At start of empty line
    if (cursorPosition > 0 && value[cursorPosition - 1] === '\n') {
        return cursorPosition >= value.length || value[cursorPosition] === '\n';
    }

    return false;
};

const performAutofill = (
    value: string,
    cursorPosition: number,
    userName: string | null | undefined,
    onChange: (value: string) => void,
    inputRef: MutableRefObject<HTMLTextAreaElement | null>
): void => {
    const basePrefix = generateAutofillPrefix(userName || null);

    // Check if first line has content
    const firstLineEnd = value.indexOf('\n');
    const firstLine = firstLineEnd === -1 ? value : value.substring(0, firstLineEnd);
    const firstLineHasContent = firstLine.trim().length > 0;
    const cursorInFirstLine = cursorPosition <= (firstLineEnd === -1 ? value.length : firstLineEnd);

    // If cursor is on the first line, and it's empty, prefix at top with leading newline
    if (cursorInFirstLine && !firstLineHasContent) {
        const prefix = `\n${basePrefix}`;
        const newValue = prefix + value;

        onChange(newValue);
        setCursorPosition(inputRef, prefix.length);
        return;
    }

    // Otherwise, insert prefix at the current cursor position (no leading newline at top)
    const newValue = value.slice(0, cursorPosition) + basePrefix + value.slice(cursorPosition);

    onChange(newValue);

    // Position cursor after the prefix (ready for user to type)
    setCursorPosition(inputRef, cursorPosition + basePrefix.length);
};

export const handleAutofillOnClick = (
    inputRef: MutableRefObject<HTMLTextAreaElement | null>,
    value: string,
    userName: string | null | undefined,
    onChange: (value: string) => void
): void => {
    if (!inputRef.current) return;

    const cursor = inputRef.current.selectionStart;
    const hasSelection = cursor !== inputRef.current.selectionEnd;

    // Only autofill when clicking (no text selection) at valid positions
    if (!hasSelection && shouldAutofillAtCursor(value, cursor)) {
        performAutofill(value, cursor, userName, onChange, inputRef);
    }
};

/**
 * Trims a string while preserving exactly one trailing newline if any trailing newlines exist.
 * This is useful for TextArea components where users may want to preserve a trailing newline
 * for formatting purposes (e.g., to ensure the cursor starts on a new line).
 *
 * @param value - The string to trim
 * @returns The trimmed string with at most one trailing newline
 *
 * @example
 * trimWithTrailingNewline("text\n\n\n") // Returns "text\n"
 * trimWithTrailingNewline("text\n") // Returns "text\n"
 * trimWithTrailingNewline("text  \n  ") // Returns "text\n"
 * trimWithTrailingNewline("text") // Returns "text"
 * trimWithTrailingNewline("  text  ") // Returns "text"
 */
export const trimWithTrailingNewline = (value: string | null | undefined): string => {
    if (!value) return '';

    let trimmed = value.trimStart();

    // Check if there are any trailing newlines (after any trailing spaces)
    let hasTrailingNewline = false;
    let i = trimmed.length - 1;

    while (i >= 0 && (trimmed[i] === ' ' || trimmed[i] === '\t' || trimmed[i] === '\r')) {
        i--;
    }

    if (i >= 0 && trimmed[i] === '\n') {
        hasTrailingNewline = true;
    }

    trimmed = trimmed.trimEnd();

    if (hasTrailingNewline) {
        trimmed += '\n';
    }

    return trimmed;
};
